<template>
    <div class="ln-grade-wrapper" ref="wrapper">
        <table class="ln-grade">
            <thead>
                <tr>
                    <th class="ln-col-num">Nº</th>
                    <th class="ln-col-aluno">Aluno</th>
                    <th v-for="d in disciplinas" :key="d.id" class="ln-col-nota" :title="d.nome">
                        {{ disciplinas.length == 1 ? 'Nota' : d.abrev }}
                    </th>
                    <th v-if="comParecer" class="ln-col-parecer">Parecer</th>
                </tr>
            </thead>
            <tbody>
                <tr v-if="!alunos.length">
                    <td :colspan="disciplinas.length + (comParecer ? 3 : 2)" class="ln-vazio">Nenhum aluno vinculado a esta turma.</td>
                </tr>
                <template v-for="{ aluno, i, anterior } in linhas">
                <tr v-if="anterior" :key="'m' + anterior.matricula_id" class="ln-saiu">
                    <td class="ln-col-num">{{ anterior.numero || '—' }}</td>
                    <td class="ln-col-aluno">
                        {{ anterior.nome }}
                        <span class="ln-etiqueta">
                            ({{ anterior.etiqueta }}<template v-if="anterior.data_etiqueta"> em {{ formatarData(anterior.data_etiqueta) }}</template>)
                        </span>
                    </td>
                    <td :colspan="disciplinas.length + (comParecer ? 1 : 0)"></td>
                </tr>
                <tr v-else :key="aluno.aluno_id" :class="{ 'ln-saiu': aluno.etiqueta }">
                    <td class="ln-col-num">{{ aluno.numero || '—' }}</td>
                    <td class="ln-col-aluno">
                        {{ aluno.nome }}
                        <span v-if="aluno.etiqueta" class="ln-etiqueta">
                            ({{ aluno.etiqueta }}<template v-if="aluno.data_etiqueta"> em {{ formatarData(aluno.data_etiqueta) }}</template>)
                        </span>
                    </td>
                    <td v-for="(d, j) in disciplinas" :key="d.id" class="ln-col-nota">
                        <nota-input
                            v-if="d.editavel && !somenteLeitura && aluno.editavel !== false"
                            v-model="aluno.notas[d.id]"
                            :data-row="i" :data-col="j"
                            :media-minima="mediaMinima"
                            :erro="!!erros[chave(aluno, d)]"
                            :conflito="!!conflitos[chave(aluno, d)]"
                            :dica="dica(aluno, d)"
                            @navegar="(dir) => mover(i, j, dir)"
                            @dblclick="aplicarConflito(aluno, d)" />
                        <span v-else class="ln-somente-leitura" :class="{ 'ln-abaixo': abaixo(aluno.notas[d.id]) }">
                            {{ formatarNota(aluno.notas[d.id]) }}
                        </span>
                    </td>
                    <td v-if="comParecer" class="ln-col-parecer">
                        <!-- Só o ícone; verde quando o parecer do bimestre já foi escrito -->
                        <el-button size="mini" :type="aluno.parecer ? 'success' : 'primary'" class="ln-btn-parecer"
                            :title="aluno.parecer ? 'Parecer preenchido — clique para ver ou editar' : 'Escrever parecer'"
                            @click="$emit('abrir-parecer', aluno)">
                            <i class="far fa-edit"
                            plain></i>
                        </el-button>
                    </td>
                </tr>
                </template>
            </tbody>
        </table>
    </div>
</template>

<script>
// Grade do lançamento de notas pela média do bimestre (spec_diario/notas/04). Por turma: uma coluna
// por disciplina; por disciplina: uma coluna "Nota". Enter/↓/↑ andam na coluna; →/← e Tab andam na linha.
// Duplo clique numa célula em conflito aplica o valor que está no banco.
import NotaInput from '@/shared/nota-input'

export default {
    components: { NotaInput },
    props: {
        disciplinas: { type: Array, required: true },
        alunos: { type: Array, required: true },
        // Matrículas anteriores do mesmo aluno na turma (saiu e voltou): linhas só de exibição, como no legado.
        anteriores: { type: Array, default: () => [] },
        erros: { type: Object, default: () => ({}) },
        conflitos: { type: Object, default: () => ({}) },
        somenteLeitura: { type: Boolean, default: false },
        mediaMinima: { type: Number, default: 5 },
        // Coluna com o botão de parecer do bimestre (turmas de nota e de parecer).
        comParecer: { type: Boolean, default: false }
    },
    computed: {
        // Alunos (com o índice real, usado na navegação por teclado) e matrículas anteriores, por nº de chamada.
        linhas () {
            const itens = this.alunos.map((aluno, i) => ({ aluno, i, anterior: null, numero: aluno.numero, nome: aluno.nome }))
            this.anteriores.forEach(a => itens.push({ aluno: null, i: -1, anterior: a, numero: a.numero, nome: a.nome }))
            return itens.sort((x, y) => {
                const nx = x.numero ? Number(x.numero) : Infinity
                const ny = y.numero ? Number(y.numero) : Infinity
                return nx === ny ? String(x.nome).localeCompare(String(y.nome)) : (nx < ny ? -1 : 1)
            })
        }
    },
    methods: {
        chave (aluno, d) {
            return `${aluno.aluno_id}-${d.id}`
        },
        dica (aluno, d) {
            const k = this.chave(aluno, d)
            if (this.conflitos[k]) {
                const c = this.conflitos[k]
                return `Alterada por ${c.por || 'outro usuário'} para ${this.formatarNota(c.valor_atual)}. Sua alteração não foi gravada. Dê um duplo clique para usar esse valor.`
            }
            return this.erros[k] || d.nome
        },
        aplicarConflito (aluno, d) {
            if (this.conflitos[this.chave(aluno, d)]) this.$emit('aplicar-conflito', { aluno, disciplina: d })
        },
        mover (linha, coluna, direcao) {
            if (!this.$refs.wrapper) return
            const delta = { baixo: [1, 0], cima: [-1, 0], direita: [0, 1], esquerda: [0, -1] }[direcao]
            let r = linha + delta[0]
            let c = coluna + delta[1]
            while (r >= 0 && r < this.alunos.length && c >= 0 && c < this.disciplinas.length) {
                const alvo = this.$refs.wrapper.querySelector(`input[data-row="${r}"][data-col="${c}"]`)
                if (alvo && !alvo.disabled) {
                    alvo.focus()
                    return
                }
                r += delta[0]
                c += delta[1]
            }
        },
        abaixo (v) {
            return v !== '' && v !== null && v !== undefined && Number(v) < this.mediaMinima
        },
        formatarNota (v) {
            if (v === '' || v === null || v === undefined) return '—'
            const n = Number(v)
            if (n === 10) return '10'
            const casas = Math.round(n * 100) % 10 === 0 ? 1 : 2
            return n.toFixed(casas).replace('.', ',')
        },
        formatarData (d) {
            return this.$moment(d).format('DD/MM/YYYY')
        }
    }
}
</script>

<style>
    .ln-grade-wrapper {
        width: 100%;
        overflow: auto;
        max-height: 70vh;
    }
    .ln-grade {
        border-collapse: separate;
        border-spacing: 0;
        font-size: 13px;
        min-width: 100%;
    }
    .ln-grade thead th {
        position: sticky;
        top: 0;
        z-index: 2;
        background: #1c69b3;
        color: #fff;
        font-weight: 600;
        padding: 6px 8px;
        text-align: center;
        white-space: nowrap;
    }
    .ln-grade tbody td {
        padding: 3px 6px;
        border-bottom: 1px solid #ebeef5;
        background: #fff;
        vertical-align: middle;
    }
    .ln-grade tbody tr:nth-child(even) td {
        background: #fafafa;
    }
    .ln-grade .ln-col-num {
        position: sticky;
        left: 0;
        z-index: 1;
        width: 40px;
        min-width: 40px;
        text-align: center;
    }
    .ln-grade .ln-col-aluno {
        position: sticky;
        left: 40px;
        z-index: 1;
        text-align: left;
        min-width: 230px;
        box-shadow: 1px 0 0 #ebeef5;
    }
    .ln-grade thead .ln-col-num,
    .ln-grade thead .ln-col-aluno {
        z-index: 3;
    }
    .ln-grade .ln-col-parecer {
        text-align: center;
        width: 80px;
        min-width: 80px;
    }
    .ln-grade .ln-btn-parecer {
        padding: 6px 12px;
    }
    .ln-grade .ln-col-nota {
        text-align: center;
        min-width: 70px;
    }
    .ln-grade tr.ln-saiu td {
        background: #ececec !important;
    }
    .ln-grade tr.ln-saiu .ln-col-aluno,
    .ln-grade .ln-etiqueta {
        font-weight: 700;
    }
    .ln-grade .ln-somente-leitura {
        color: #606266;
    }
    .ln-grade .ln-abaixo {
        color: #f56c6c;
        font-weight: 600;
    }
    .ln-grade .ln-vazio {
        text-align: center;
        color: #909399;
        padding: 20px;
    }
</style>
